import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import isEmpty from 'lodash.isempty';
import { getValidationSchema } from '../../util/helpers/getValidationSchema';
import useStepPost from '../../hooks/useStepPost';
import { useApplicantData } from '../../hooks/useApplicantData';
import { FormContainer, FormWrap } from '../FormContainer/FormContainer';
import useBusinessNameDispatch from '../../hooks/useBusinessNameDispatch';
import useIraApplicationData from '../../hooks/useIraApplicationData';
import { APPLICANT_TYPES } from '../../util/constants';
import InputText from '../Fields/InputText';
import OnBoardingCta from '../OnBoarding/OnBoardingCta';
import { getValidationSchemaImpersonate } from '../../util/helpers/getValidationSchemaImpersonate';
import { retrieveFromSessionStorage } from '../../util/helpers/sessionStorageHelper';
import {
  ACCOUNT_TYPE_GTM,
  IS_SALES_ASSESSTED_REGISTRATION_FLOW,
} from '../../constants/sessionStorage';
import SaveAndCloseModal from '../Modals/SaveAndCloseModal/SaveAndCloseModal';
import useGtmStepHook from '../../util/hooks/useGtmStepHook';
import { gtmStepHelper } from '../../util/helpers/gtmHelper';
import { ACCOUNT_STEP_COMPLETE, ACCOUNT_STEP_VIEW } from '../../util/gtmEvents';

const formValidationSchema = Yup.object({
  ...getValidationSchema({
    fields: ['BusinessName'],
  }),
});

const formValidationSchemaImpersonate = Yup.object({
  ...getValidationSchemaImpersonate({
    fields: ['BusinessName'],
  }),
});

const RetirementBusinessNameForm = ({
  nextPath,
  formId,
  setEventType,
  eventType,
  onModalClose,
}) => {
  const { t } = useTranslation();
  const { onSuccess } = useStepPost({ nextPath, onModalClose });
  const { businessNameDispatch } = useBusinessNameDispatch();
  const { IRAApplication } = useIraApplicationData();
  const ApplicantData = useApplicantData(APPLICANT_TYPES.MAIN);
  const isSalesAssisted = retrieveFromSessionStorage(
    IS_SALES_ASSESSTED_REGISTRATION_FLOW,
  );
  const [saveAndCloseModalOpen, setSaveAndCloseModalOpen] = useState(false);
  const [hasError, setHasError] = useState(false);

  const { gtmStepEvent } = useGtmStepHook();
  const accountGtmType = retrieveFromSessionStorage(ACCOUNT_TYPE_GTM);

  useEffect(() => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_VIEW,
      stepLabel: 'Retirement Business Name',
      accountType: accountGtmType,
      ...(isEmpty(ApplicantData?.PersonName?.FirstName)
        ? {}
        : { accountName: ApplicantData?.PersonName }),
      ...(isEmpty(ApplicantData?.PersonBirthday?.DateOfBirth)
        ? {}
        : { accountDateOfBirth: ApplicantData?.PersonBirthday }),
      customParams: {
        timestamp: new Date().toISOString(),
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGtmEvent = () => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_COMPLETE,
      stepLabel: 'Retirement Business Name',
      accountType: accountGtmType,
      accountName: ApplicantData?.PersonName,
      accountDateOfBirth: ApplicantData?.PersonBirthday,
      customParams: {
        timestamp: new Date().toISOString(),
      },
    });
  };

  const defaultValues = {
    BusinessName: IRAApplication?.CompanyName || '',
  };

  const { handleSubmit, control } = useForm({
    resolver: yupResolver(
      isSalesAssisted ? formValidationSchemaImpersonate : formValidationSchema,
    ),
    defaultValues,
  });

  const onSubmit = ({ BusinessName }) => {
    businessNameDispatch({
      data: {
        BusinessName,
      },
      defaultValues,
      onSuccess:
        eventType === 'Form'
          ? onSuccess
          : () => {
              setHasError(false);
              setSaveAndCloseModalOpen(true);
            },
      onError: () => {
        setHasError(true);
        setSaveAndCloseModalOpen(true);
      },
    });
    handleGtmEvent();
  };

  return (
    <FormWrap id={formId} onSubmit={handleSubmit(onSubmit)}>
      <FormContainer>
        <Controller
          control={control}
          render={({ field, fieldState }) => (
            <InputText
              label={t('retirementBusinessName.businessName')}
              {...field}
              {...fieldState}
              autoFocus
            />
          )}
          name="BusinessName"
        />
      </FormContainer>
      <OnBoardingCta
        type="submit"
        onClick={() => setEventType('Form')}
        title={t('continue')}
      />
      {saveAndCloseModalOpen && (
        <SaveAndCloseModal
          isOpen={saveAndCloseModalOpen}
          onClose={() => setSaveAndCloseModalOpen(false)}
          hasError={hasError}
          onSuccess={onSuccess}
        />
      )}
    </FormWrap>
  );
};

RetirementBusinessNameForm.propTypes = {
  nextPath: PropTypes.string,
  applicantType: PropTypes.string,
  formId: PropTypes.string,
  eventType: PropTypes.string,
  setEventType: PropTypes.func,
  onModalClose: PropTypes.func,
};

export default RetirementBusinessNameForm;
